import React, { ReactNode, CSSProperties, memo, useMemo } from 'react';
import { Handle, Position, NodeResizer, useEdges, useConnection } from '@xyflow/react';
import { DeleteIcon, ContentCopyIcon, InfoIcon, SettingsIcon, WarningIcon, CheckCircleIcon } from './nodeIcons';

/**
 * BaseNode Component
 * Wrapper providing common functionality for all nodes
 */

export interface BaseNodeProps {
  id: string;
  selected?: boolean;
  children: ReactNode;
  
  // Node metadata
  title?: string;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple' | 'warning' | 'orange';
  outputType?: 'text' | 'image' | 'video' | 'audio';  // Output media type for type-based styling
  
  // Status bar
  status?: 'idle' | 'processing' | 'success' | 'error' | 'warning';
  statusMessage?: string;
  
  // Handles configuration
  hasInputHandle?: boolean;
  hasOutputHandle?: boolean;
  inputHandlePosition?: Position;
  outputHandlePosition?: Position;
  inputLabel?: string;
  outputLabel?: string;
  // Multiple input handles support
  multipleInputHandles?: Array<{
    id: string;
    label: string;
    type: string;
    position?: Position;
  }>;
  
  // Actions
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  headerButtons?: ReactNode;
  
  // Resizing
  resizable?: boolean;
  minWidth?: number;
  minHeight?: number;
  keepAspectRatio?: boolean; // Lock aspect ratio during resize (for image/video content)
  
  // Styling
  className?: string;
  style?: CSSProperties;
  headerStyle?: CSSProperties;
  customIcon?: string;
}

const BaseNodeComponent: React.FC<BaseNodeProps> = ({
  id,
  selected = false,
  children,
  title,
  variant = 'neutral',
  outputType,
  status = 'idle',
  statusMessage = 'Ready',
  hasInputHandle = true,
  hasOutputHandle = true,
  inputHandlePosition = Position.Left,
  outputHandlePosition = Position.Right,
  inputLabel,
  outputLabel,
  multipleInputHandles,
  onDelete,
  onDuplicate,
  headerButtons,
  resizable = false,
  minWidth = 320,
  minHeight = 200,
  keepAspectRatio = false,
  className = '',
  style = {},
  headerStyle = {},
  customIcon,
}) => {
  // Get all edges to check which handles are connected
  const edges = useEdges();
  
  // Get current connection state (for dragging detection)
  const connection = useConnection();
  
  // Check if output handle is connected (has outgoing edges from this node)
  const isOutputConnected = useMemo(() => {
    return edges.some(edge => edge.source === id);
  }, [edges, id]);
  
  // Check if a connection is being dragged FROM this node's output handle
  const isOutputDragging = useMemo(() => {
    return connection.inProgress && 
           connection.fromNode?.id === id && 
           connection.fromHandle?.type === 'source';
  }, [connection.inProgress, connection.fromNode?.id, connection.fromHandle?.type, id]);
  
  // Check if input handle is connected (has incoming edges to this node)
  const isInputConnected = useMemo(() => {
    return edges.some(edge => edge.target === id);
  }, [edges, id]);
  
  // Check if a connection is being dragged and is near/targeting this node's input
  // React Flow adds .valid class when hovering, but we track it here for more control
  const isInputNear = useMemo(() => {
    return connection.inProgress && 
           connection.toNode?.id === id;
  }, [connection.inProgress, connection.toNode?.id, id]);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(id);
    }
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDuplicate) {
      onDuplicate(id);
    }
  };

  const variantClass = `base-node-${variant}`;
  const selectedClass = selected ? 'base-node-selected' : '';
  const outputTypeClass = outputType ? `base-node-type-${outputType}` : '';

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <SettingsIcon size={14} className="status-icon-spinning" />;
      case 'error':
        return <WarningIcon size={14} />;
      case 'success':
        return <CheckCircleIcon size={14} />;
      case 'idle':
      default:
        return <InfoIcon size={14} />;
    }
  };

  return (
    <div
      className={`base-node ${variantClass} ${outputTypeClass} ${selectedClass} ${className}`.trim()}
      style={style}
    >
      {/* React Flow Resize Control */}
      {resizable && (
        <NodeResizer
          minWidth={minWidth}
          minHeight={minHeight}
          keepAspectRatio={keepAspectRatio}
        />
      )}

      {/* Node Header */}
      {title && (
        <div className="base-node-header" style={headerStyle}>
          <div className="base-node-header-content">
            {outputType && <span className={`base-node-type base-node-type-label-${outputType}`}>{outputType.toUpperCase()}</span>}
            {customIcon && <span className="base-node-custom-icon">{customIcon}</span>}
            <span className="base-node-title">{title}</span>
          </div>
          
          <div className="base-node-actions">
            {headerButtons}
            {onDuplicate && (
              <button
                className="base-node-action-button"
                onClick={handleDuplicate}
                title="Duplicate node"
              >
                <ContentCopyIcon size={18} />
              </button>
            )}
            {onDelete && (
              <button
                className="base-node-action-button base-node-delete-button"
                onClick={handleDelete}
                title="Delete node"
              >
                <DeleteIcon size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Status Bar */}
      <div className="node-status-bar" data-status={status}>
        <div className="status-primary">
          <span className="status-icon">
            {getStatusIcon()}
          </span>
          <span className="status-text">{statusMessage}</span>
        </div>
      </div>

      {/* Input Handles - positioned on left edge */}
      {multipleInputHandles ? (
        // Multiple input handles (stacked vertically on left edge)
        multipleInputHandles.map((handle, index) => {
          // Determine per-handle connection state
          const handleConnected = edges.some(edge => edge.target === id && edge.targetHandle === handle.id);
          // Check if THIS SPECIFIC handle is being targeted
          const handleNear = connection.inProgress && 
                            connection.toNode?.id === id && 
                            connection.toHandle?.id === handle.id;
          const handleInputState = handleConnected ? 'closed' : (handleNear ? 'closing' : 'open');
          return (
            <Handle
              key={handle.id}
              type="target"
              position={handle.position || Position.Left}
              id={handle.id}
              className={`base-node-handle base-node-handle-input base-node-handle-${variant} base-node-handle-type-${handle.type} base-node-handle-input-${handleInputState}`}
              style={{ top: `${128 + index * 40}px` }}
            >
              <span className={`base-node-edge-label base-node-edge-label-input base-node-connector-type-${handle.type}`}>
                {handle.label}
              </span>
            </Handle>
          );
        })
      ) : hasInputHandle ? (
        // Single input handle - determine state: open (unconnected), closing (near), closed (connected)
        (() => {
          const inputState = isInputConnected ? 'closed' : (isInputNear ? 'closing' : 'open');
          return (
            <Handle
              type="target"
              position={inputHandlePosition}
              className={`base-node-handle base-node-handle-input base-node-handle-${variant} base-node-handle-type-${inputLabel?.toLowerCase() || 'any'} base-node-handle-input-${inputState}`}
              style={{ top: '128px' }}
            >
              <span className={`base-node-edge-label base-node-edge-label-input base-node-connector-type-${inputLabel?.toLowerCase() || 'any'}`}>
                {inputLabel}
              </span>
            </Handle>
          );
        })()
      ) : null}

      {/* Output Handle - positioned on right edge */}
      {hasOutputHandle && (
        <Handle
          type="source"
          position={outputHandlePosition}
          className={`base-node-handle base-node-handle-output base-node-handle-${variant} base-node-handle-type-${outputLabel?.toLowerCase() || 'any'}${isOutputConnected || isOutputDragging ? ' base-node-handle-active' : ''}`}
          style={{ top: '128px' }}
        >
          <span className={`base-node-edge-label base-node-edge-label-output base-node-connector-type-${outputLabel?.toLowerCase() || 'any'}`}>
            {outputLabel}
          </span>
        </Handle>
      )}

      {/* Node Content */}
      <div className="base-node-content">
        {children}
      </div>
    </div>
  );
};

/**
 * Memoized BaseNode to prevent unnecessary re-renders
 */
export const BaseNode = memo(BaseNodeComponent);
